import type { ReactElement } from 'react';
import { usePrintMethodsQuery } from '@/entities/print-method';
import { useInvalidateProducts } from '@/entities/product';
import { useToggleSet } from '@/shared/lib';
import { Button, cn } from '@/shared/ui';
import { useSetPrintMethods } from '../model/use-set-print-methods';
import { SectionCard } from './section-card';

interface PrintMethodSectionProps {
  readonly productId: string;
  readonly printMethodIds: readonly string[];
  readonly onSaved: (message: string) => void;
}

/** 지원 인쇄 방식 다중 선택 — 마스터는 기준 정보 관리에서 편집한다. */
export const PrintMethodSection = ({
  productId,
  printMethodIds,
  onSaved,
}: PrintMethodSectionProps): ReactElement => {
  const invalidate = useInvalidateProducts();
  const methodsQuery = usePrintMethodsQuery();
  const saveAction = useSetPrintMethods({
    onSuccess: () => {
      onSaved('인쇄 방식을 저장했습니다.');
      invalidate.detail(productId);
    },
  });

  const { selected, toggle } = useToggleSet(printMethodIds);

  return (
    <SectionCard title="인쇄 방식" error={saveAction.error?.message ?? null}>
      {methodsQuery.isPending ? (
        <p className="text-muted-foreground">불러오는 중…</p>
      ) : methodsQuery.isError ? (
        <p role="alert" className="text-destructive">
          인쇄 방식을 불러오지 못했습니다.
        </p>
      ) : (
        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap gap-2">
            {methodsQuery.data.map((method) => {
              const isSelected = selected.has(method.id);

              return (
                <button
                  key={method.id}
                  type="button"
                  aria-pressed={isSelected}
                  onClick={() => toggle(method.id)}
                  className={cn(
                    'rounded-full border px-3 py-1 text-sm transition-colors',
                    isSelected
                      ? 'border-foreground bg-foreground text-background'
                      : 'text-muted-foreground hover:bg-accent hover:text-foreground',
                  )}
                >
                  {method.name}
                </button>
              );
            })}
          </div>

          <div>
            <Button
              size="sm"
              isLoading={saveAction.isPending}
              onClick={() =>
                saveAction.setPrintMethods({ id: productId, printMethodIds: [...selected] })
              }
            >
              인쇄 방식 저장
            </Button>
          </div>
        </div>
      )}
    </SectionCard>
  );
};
